import type {
    UserRegisterRequest,
    UserUpdatePasswordRequest,
    UserUpdatePasswordResponse,
    SuccessResponse,
    LoginResponse,
} from '@/api/types/Auth';
import type { Resource } from '@/api/model/Resource';
import type { UserJson } from '@/api/model/User';
import { User } from '@/api/model/User';
import { apiFetch } from '@/lib/http';

async function ensureCsrfCookie(): Promise<void> {
    await apiFetch<void>('/csrf-cookie', { responseType: 'void' });
}

export default function useAuthService() {
    return {
        getCurrentUser(): Promise<User> {
            return apiFetch<Resource<UserJson>>(`/api/users/me`).then((response) => User.fromJSON(response.data));
        },

        async login(email: string, password: string): Promise<LoginResponse> {
            await ensureCsrfCookie();
            const response = await apiFetch<LoginResponse>(`/api/fortify/login`, {
                method: 'POST',
                body: {
                    email: email,
                    password: password,
                },
            });
            // Fetch a fresh CSRF cookie via GET so that Passport's
            // CreateFreshApiToken middleware attaches the laravel_token
            // JWT cookie needed for subsequent API requests.
            await ensureCsrfCookie();
            return response;
        },

        async register(credentials: UserRegisterRequest): Promise<void> {
            await ensureCsrfCookie();
            await apiFetch<void>(`/api/fortify/register`, {
                method: 'POST',
                body: credentials,
                responseType: 'void',
            });
            await ensureCsrfCookie();
        },

        async logout(): Promise<void> {
            await apiFetch<void>('/api/fortify/logout', {
                method: 'POST',
                responseType: 'void',
            });
        },

        async createPassword(createData: UserUpdatePasswordRequest): Promise<number> {
            await ensureCsrfCookie();
            const response = await apiFetch<UserUpdatePasswordResponse>('/api/signup/create-password', {
                method: 'POST',
                body: createData,
                responseType: 'void',
                returnResponse: true,
            });

            return response.status;
        },

        async forgotPassword(email: string): Promise<SuccessResponse> {
            await ensureCsrfCookie();
            return apiFetch<SuccessResponse>('/api/fortify/forgot-password', {
                method: 'POST',
                body: {
                    email: email,
                },
            });
        },

        async resetPassword(
            email: string,
            token: string,
            password: string,
            password_confirmation: string,
        ): Promise<SuccessResponse> {
            await ensureCsrfCookie();
            return apiFetch<SuccessResponse>('/api/fortify/reset-password', {
                method: 'POST',
                body: {
                    email: email,
                    token: token,
                    password: password,
                    password_confirmation: password_confirmation,
                },
            });
        },

        async requestEmailVerificationLink(email: string): Promise<SuccessResponse> {
            await ensureCsrfCookie();
            return apiFetch<SuccessResponse>('/api/fortify/email/verification-notification', {
                method: 'POST',
                body: {
                    email: email,
                },
            });
        },
    };
}
